import { isEmpty, set, get } from 'lodash';

const fpPromise = window.FingerprintJS.load();

import { setCookie, getCookie, getSearchParam } from '@shared/utils';
import { IFlatNode, IPayload, IExpressBuy } from '@shared/types';

import { textConstant, UTM_KEYS } from '@/constants';
import { Notification } from '@/widgets';
import { IMerchantInfo } from '@/types';

import Constants from './constants';
import getErrorMessage from './errorMessages';
import { sendClickEvent } from './AnalyticsUtils';

interface IParsedError {
	code?: string;
	errorCode?: string;
	message?: string;
	errors?: string[];
	[key: string]: unknown;
}

export interface IInput {
	active?: boolean;
	entity?: string;
	value?: string;
}

interface ICopyToClipboardConfig {
	successMessage?: string;
	errorMessage?: string;
	onSuccess?: () => void;
	onFailed?: () => void;
	successEventData?: {
		name: string;
		value: string;
	};
	failedEventData?: {
		name: string;
		value: string;
	};
	showToast?: boolean;
}

/**
 * NestedRecord is a recursive type definition that represents an object with string keys,
 * where each key maps to another NestedRecord. This allows for deeply nested objects
 */

type NestedRecord<T = unknown> = { [key: string]: NestedRecord<T> };

export default async function handleError(error: {
	data: { message?: string; code?: string; text?: () => Promise<string> };
	status: number | string;
}) {
	const { data, status } = error;

	let parsedError: IParsedError;
	try {
		parsedError = JSON.parse(await data.text!());
	} catch {
		parsedError = typeof data === 'object' ? { ...data } : data;
	}

	const displayErrorMessage =
		(getErrorMessage(parsedError.code!) ||
			getErrorMessage(parsedError.errorCode!) ||
			parsedError?.message) ??
		(get(parsedError, 'errors.0') || getErrorMessage(status, 'HTTP_ERROR'));

	return {
		...(typeof data === 'object' ? data : {}),
		status: status,
		message: displayErrorMessage ?? textConstant.SOMETHING_WENT_WRONG,
	};
}

// General utility function to test presence of variables in chained object access eg. obj1.obj2.obj3 or obj1.obj2['obj3']
export function getObjValue<T = object, R extends object = object>(
	rootObj: R,
	keyChainStr: string,
): T {
	if (!keyChainStr || keyChainStr === '') return rootObj as unknown as T;

	// iterating the key chain one by one
	const keys = keyChainStr.replace(/]/g, '').split(/[.[]/);

	return keys.reduce((o, ele) => {
		if (ele === '') return o;
		return !o || typeof o === 'undefined' || o === null ? o : o[ele];
	}, rootObj as NestedRecord) as T;
}

// trim extra data from values to show in table cells
export function trimExtraData(value: string) {
	if (value && value.length > 40) return `${value.slice(0, 40)}...`;
	return value;
}

// Transforms text to first character capital and others small
export function capitalize(value: string) {
	if (value.length) {
		return (
			value[0].toLocaleUpperCase() +
			value.slice(1, value.length).toLocaleLowerCase()
		);
	}
	return value;
}

export function arrayFromJson(
	object: IInput[] | null,
	keys: Record<string, string[]>,
) {
	if (object && object.length > 0) {
		object.forEach((obj) => {
			if (obj.active) {
				const { entity, value } = obj;
				if (Object.keys(keys).includes(entity!)) {
					keys[entity!].push(value!);
				}
			}
			return 0;
		});
	}
	return keys;
}

// Converts query string to JSON format
export function queryStringToJSON(queryString: string | undefined) {
	if (queryString) {
		const returnObj: Record<string, string> = {};
		const params = decodeURI(queryString)
			.replace('?', '')
			.split('&')
			.map((param) => param.split('='))
			.reduce((_, [key, value]) => {
				returnObj[key] = value;
				return returnObj;
			}, {});
		return params;
	}
	return {};
}

export function groupTypeCheck(groupValue: string) {
	return groupValue === Constants.SUPERMERCHANT_GROUP_NAME;
}

// get vas detail
export const getVasDetail = (
	vasDetail: Array<{
		displayName: string;
	}>,
) => {
	if (vasDetail && vasDetail.length > 0) {
		return vasDetail.map((detail) => detail.displayName).join(',');
	}
	return 'Sale';
};

// truncate
export const truncate = (string: string, size: number) =>
	string.length > size ? `${string.substring(0, size - 1)}...` : string;

export function cryptoRandom() {
	const typedArray = new Uint8Array(1);
	const randomValue = crypto.getRandomValues(typedArray)[0];
	const randomFloat = randomValue / Math.pow(2, 8);
	return randomFloat;
}

export const generateUniqueString = (length = 36) => {
	let result = '';
	const characters =
		'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
	const charactersLength = characters.length;
	let counter = 0;
	while (counter < length) {
		result += characters.charAt(Math.floor(cryptoRandom() * charactersLength));
		counter += 1;
	}
	return result;
};

export const generateBrowserFingerprint = (): Promise<string> =>
	new Promise((resolve, reject) => {
		fpPromise
			.then((fp) => fp.get())
			.then((result) => {
				const { visitorId } = result;
				const uniqueVisitorId = `pbweb_${visitorId}_${generateUniqueString(5)}`;
				/**
				 * Setted the fingerprint cookie as 2 month because backend also keep the session for 2 months
				 * and after 2 months the session will forced logout from backend.
				 */
				setCookie(Constants.FINGERPRINT_COOKIE, uniqueVisitorId, {
					expires: {
						days: 365,
					},
				});
				resolve(uniqueVisitorId);
			})
			.catch((error: string) => {
				reject(new Error(error));
			});
	});

export const getFingerprintCookie = () =>
	getCookie(Constants.FINGERPRINT_COOKIE);

export const refreshFingerprintToken = () => {
	const fingerprintCookie = getFingerprintCookie();
	const fpUnderscoreIndex = fingerprintCookie?.lastIndexOf('_') ?? -1;
	const isNewFingerprint =
		fpUnderscoreIndex > -1 &&
		fingerprintCookie.slice(fpUnderscoreIndex + 1).length === 5;
	if (!isNewFingerprint) {
		generateBrowserFingerprint();
	}
};

export const getTrackerId = (merchantType: string) => {
	return merchantType === Constants.ONLINE_MERCHANT
		? Constants.TRACKER_ID_ONLINE_DASHBOARD
		: Constants.TRACKER_ID_OFFLINE_DASHBOARD;
};

export const deleteMultipleKeysInObject = (
	object: Record<string, unknown>,
	keysToRemove: Array<string>,
) => {
	if (isEmpty(keysToRemove)) {
		return object;
	}

	keysToRemove.forEach((cur) => delete object[cur]);
	return object;
};

export const isExternalUrl = (href: string) => {
	return (
		href.startsWith('http') ??
		href.startsWith('mailto:') ??
		href.startsWith('tel:')
	);
};

//NOTE: move to shared and align with galactus helper contract
export const copyToClipboard = (
	text: string,
	config: ICopyToClipboardConfig,
) => {
	const {
		successMessage = 'Text copied successfully.',
		errorMessage = 'Unable to copy the text. Please try again.',
		onSuccess,
		onFailed,
		successEventData,
		failedEventData,
		showToast = true,
	} = config;

	const handleSuccess = () => {
		if (showToast) {
			Notification.show({ type: 'success', message: successMessage });
		}
		if (successEventData?.name && successEventData?.value) {
			sendClickEvent({
				name: successEventData.name,
				value: successEventData.value,
			});
		}
		onSuccess && onSuccess();
	};

	const handleFailed = () => {
		if (showToast) {
			Notification.show({ type: 'error', message: errorMessage });
		}
		if (!isEmpty(failedEventData)) {
			sendClickEvent({
				name: failedEventData.name,
				value: failedEventData.value,
			});
		}
		onFailed && onFailed();
	};

	const handleClipboardWrite = () => {
		navigator.clipboard.writeText(text).then(handleSuccess).catch(handleFailed);
	};

	const handleClipboardNotSupported = () => {
		sendClickEvent({ name: 'COPY_FEATURE_NOT_SUPPORTED' });
		Notification.show({ type: 'error', message: 'Feature not supported' });
	};

	if (navigator.clipboard) {
		handleClipboardWrite();
	} else {
		handleClipboardNotSupported();
	}
};

export const getUserRoleNameByID = (
	roleList: Array<IMerchantInfo>,
	roleId: string,
) => {
	return roleList?.find((cur) => cur.roleId === roleId);
};

export const getInputType = (inputValue: string) => {
	let channelType = '';
	if (Constants.MOBILE_REGEX.test(inputValue)) {
		channelType = 'SMS';
	}
	if (Constants.EMAIL_REGEX.test(inputValue)) {
		channelType = 'EMAIL';
	}
	return channelType;
};

export const filterUserGroupsByStatus = (
	userGroups: Array<{
		userGroupNamespace: { status: string };
	}>,
) => {
	return userGroups.filter(
		(cur) => cur.userGroupNamespace.status !== 'INVITED',
	);
};

export const filterUserConfigData = <T extends Record<string, IFlatNode>>(
	payload: IPayload<T>,
) => {
	const config: Record<string, unknown> = {};
	const evaluations = get(payload, 'evaluations', []);

	evaluations.forEach((data) => {
		const node = data.flatNodes;
		const keyName: string = Object.keys(node)[0];
		config[keyName] = get(node, `${keyName}.flatNode.value.value`, '');
	});

	return config;
};
export const getCMSFaqUrl = (
	merchantType: 'ONLINE_MERCHANT' | 'OFFLINE_MERCHANT',
) => {
	if (merchantType === Constants.ONLINE_MERCHANT) {
		return process.env.CMS_FAQS_URL_ONLINE_MER;
	}

	return process.env.CMS_FAQS_URL_OFFLINE_MER;
};

/**
 *
 * @returns { import('@/types/Merchant.types').MerchantType }
 */
export function getMerchantType(
	merchantSubType:
		| (typeof ONLINE_MERCHANT_TYPES)[number]
		| typeof OFFLINE_MERCHANT,
) {
	const { ONLINE_MERCHANT_TYPES, ONLINE_MERCHANT, OFFLINE_MERCHANT } =
		Constants;

	if (merchantSubType === OFFLINE_MERCHANT) return OFFLINE_MERCHANT;

	return ONLINE_MERCHANT_TYPES.includes(merchantSubType)
		? ONLINE_MERCHANT
		: OFFLINE_MERCHANT;
}

export function safeSet<O extends object = object, V = unknown>(
	object: O,
	path: string,
	value: V,
) {
	if (path !== undefined && value !== undefined) {
		set(object, path, value);
	}
}

export function refundAllowed(
	refundAccess: boolean,
	merchantType: string,
	paymentState: string,
	autoFailed: boolean,
	totalRefunded: number,
	amount: number,
	expressBuy?: IExpressBuy,
) {
	if (!refundAccess || merchantType === 'P2M_LIMITED') {
		return { errorText: 'Not Authorized to Refund.', isAuthorized: false };
	}

	if (paymentState !== 'COMPLETED' || autoFailed) {
		const errorText = expressBuy
			? `${expressBuy.state} TRANSACTION!`
			: `${paymentState} TRANSACTION!`;
		return { errorText, isAuthorized: false };
	}

	if (totalRefunded >= amount) {
		return {
			errorText: 'Refund is not allowed. Refundable amount is ₹0',
			isAuthorized: false,
		};
	}

	return { errorText: null, isAuthorized: true };
}

export function calculateTotalRefund(
	refundDetails: Array<Record<string, string | number>> = [],
) {
	let totalRefunded = 0;

	refundDetails?.forEach((key) => {
		const restrictedStates = [
			Constants.STATUSES.FAILED,
			Constants.STATUSES.CANCELLED,
			Constants.STATUSES.ERRORED,
		];

		if (!restrictedStates.includes(key.paymentState as string)) {
			totalRefunded += key.amount as number;
			totalRefunded += key.offerAdjustment as number;
		}
	});

	return totalRefunded;
}

/**
 * Returns object with utm params
 * @returns {object} Object with utm params
 */
export const getUTMData = () => {
	const searchParams = window.location.search;
	const utmSource = getSearchParam(searchParams, UTM_KEYS.UTM_SOURCE);
	const utmData = { ...(utmSource && { utm_source: utmSource }) };
	return utmData;
};

/**
 * Splits the given FAQ URL into different query levels.
 *
 * This function checks the provided URL and determines if it contains
 * specific query parameters for different FAQ page levels (l1, l2, l3).
 *
 * @param url - The URL string to be split.
 * @returns An object containing the query parameters for different levels:
 * The object will have one of the following properties:
 * - `l1Query`: The query for level 1 FAQ.
 * - `l2Query`: The query for level 2 FAQ.
 * - `l3Query`: The query for level 3 FAQ.
 */
export const splitFAQUrl = (
	url: string,
): { l1Query?: string; l2Query?: string; l3Query?: string } => {
	let l1FAQQuery;
	let l2FAQQuery;
	let l3FAQQuery;
	if (url.includes('faqDescription?query=')) {
		l3FAQQuery = url.replace('/faqDescription?query=', '');
	} else if (url.includes('faq?query=')) {
		l2FAQQuery = url.replace('/faq?query=', '');
	} else {
		l1FAQQuery = url.replace('/', '');
	}

	if (l3FAQQuery) {
		return { l3Query: l3FAQQuery };
	} else if (l2FAQQuery) {
		return { l2Query: l2FAQQuery };
	}
	return { l1Query: l1FAQQuery };
};

export const dataParser = <T extends string | object>(
	rawData: object | string,
): T => {
	if (typeof rawData !== 'string') {
		return rawData as T;
	}
	try {
		return JSON.parse(rawData) as T;
	} catch (e) {
		console.error('Failed to parse data:', e);
		return {} as T;
	}
};
